import { Fragment, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  CalendarRangeIcon,
  ChevronDownIcon,
  DownloadIcon,
  FilterIcon,
  Loader2Icon,
  MapPinIcon,
  TargetIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import type { DashboardRegionRow } from '@/lib/auth-client';
import { getDashboardStats, type DashboardStatsResponse } from '@/lib/auth-client';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { SortHeaderButton, ToolbarButton } from '@/components/ui/table-shared';

type SessionData = {
  user: {
    role?: string | null;
  };
};

type DashboardFilters = {
  endDate: string;
  regionIds: string[];
  salonIds: string[];
  startDate: string;
};

type SortKey =
  | 'averageParticipantsPerSalon'
  | 'evolution'
  | 'participationRate'
  | 'regionName'
  | 'salonCount'
  | 'totalParticipants';

type SortRule = {
  direction: 'asc' | 'desc';
  key: SortKey;
};

const DEFAULT_FILTERS: DashboardFilters = {
  endDate: '',
  regionIds: [],
  salonIds: [],
  startDate: '',
};

const DEFAULT_SORTS: SortRule[] = [{ key: 'totalParticipants', direction: 'desc' }];
const pageSizeOptions = [10, 25, 50] as const;

function getPaginationItems(page: number, pageCount: number) {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, pageCount, page, page - 1, page + 1]);

  if (page <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }

  if (page >= pageCount - 2) {
    pages.add(pageCount - 1);
    pages.add(pageCount - 2);
    pages.add(pageCount - 3);
  }

  return Array.from(pages).filter((value) => value >= 1 && value <= pageCount).sort((left, right) => left - right);
}

function formatPercent(value: number | null) {
  if (value === null) {
    return '—';
  }

  return `${value.toLocaleString('fr-FR', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })}%`;
}

function formatEvolution(value: number | null) {
  if (value === null) {
    return '—';
  }

  const prefix = value > 0 ? '+' : '';
  return `${prefix}${formatPercent(value)}`;
}

function escapeXml(value: string | number) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function workbookWorksheetXml(title: string, rows: Array<Array<string | number>>) {
  const body = rows
    .map((row) => (
      `<Row>${row.map((cell) => `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`).join('')}</Row>`
    ))
    .join('');

  return `<Worksheet ss:Name="${escapeXml(title)}"><Table>${body}</Table></Worksheet>`;
}

function exportDashboardWorkbook(data: DashboardStatsResponse) {
  const summaryRows = [
    ['Indicateur', 'Valeur'],
    ...data.export.summary.map((item) => [item.label, String(item.value)]),
  ];

  const regionRows = [
    ['Region ID', 'Région', 'Nb salons', 'Total participants', 'Taux participation', 'Moy. participants/salon', 'Évolution', 'Statut'],
    ...data.export.regions.map((item) => [
      item.regionId,
      item.regionName,
      item.salonCount,
      item.totalParticipants,
      formatPercent(item.participationRate),
      item.averageParticipantsPerSalon,
      formatEvolution(item.evolution),
      item.status,
    ]),
  ];

  const salonRows = [
    ['Salon ID', 'Salon', 'Region ID', 'Région', 'Participants', 'Taux participation', 'Statut', 'Début', 'Fin', 'Créé le', 'Mis à jour le'],
    ...data.export.salons.map((item) => [
      item.salonId,
      item.salonName,
      item.regionId,
      item.regionName,
      item.participantCount,
      formatPercent(item.participationRate),
      item.status,
      item.startDate ?? '',
      item.endDate ?? '',
      item.createdAt,
      item.updatedAt,
    ]),
  ];

  const workbook = `<?xml version="1.0"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
${workbookWorksheetXml('Résumé', summaryRows)}
${workbookWorksheetXml('Détail par région', regionRows)}
${workbookWorksheetXml('Détail par salon', salonRows)}
</Workbook>`;

  const blob = new Blob([workbook], {
    type: 'application/vnd.ms-excel;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `dashboard-participants-${new Date().toISOString().slice(0, 10)}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function compareRows(left: DashboardRegionRow, right: DashboardRegionRow, sort: SortRule) {
  if (sort.key === 'regionName') {
    return left.regionName.localeCompare(right.regionName, 'fr', { sensitivity: 'base' });
  }

  if (sort.key === 'salonCount') {
    return left.salonCount - right.salonCount;
  }

  if (sort.key === 'participationRate') {
    return left.participationRate - right.participationRate;
  }

  if (sort.key === 'averageParticipantsPerSalon') {
    return left.averageParticipantsPerSalon - right.averageParticipantsPerSalon;
  }

  if (sort.key === 'evolution') {
    return (left.evolution ?? Number.NEGATIVE_INFINITY) - (right.evolution ?? Number.NEGATIVE_INFINITY);
  }

  return left.totalParticipants - right.totalParticipants;
}

export function DashboardPage({ session }: { session: SessionData }) {
  const isAdmin = (session.user.role ?? 'user') === 'admin';
  const [data, setData] = useState<DashboardStatsResponse | null>(null);
  const [draftFilters, setDraftFilters] = useState<DashboardFilters>(DEFAULT_FILTERS);
  const [filters, setFilters] = useState<DashboardFilters>(DEFAULT_FILTERS);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof pageSizeOptions)[number]>(10);
  const [sorts, setSorts] = useState<SortRule[]>(DEFAULT_SORTS);
  const hasLoadedOnceRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboard(nextFilters: DashboardFilters) {
      if (!hasLoadedOnceRef.current && !cancelled) {
        setIsLoading(true);
      }

      const { data: response, error } = await getDashboardStats(nextFilters);

      if (cancelled) {
        return;
      }

      if (error || !response?.data) {
        toast.error('Erreur lors du chargement du tableau de bord.');
        setIsLoading(false);
        return;
      }

      setData(response.data);
      setDraftFilters({
        endDate: response.data.appliedFilters.endDate,
        regionIds: response.data.appliedFilters.regionIds,
        salonIds: response.data.appliedFilters.salonIds,
        startDate: response.data.appliedFilters.startDate,
      });
      hasLoadedOnceRef.current = true;
      setIsLoading(false);
    }

    void loadDashboard(filters);

    return () => {
      cancelled = true;
    };
  }, [filters]);

  const regionOptions = data?.filterOptions.regions ?? [];
  const salonOptions = useMemo(() => {
    if (!data) {
      return [];
    }

    const regionScope = draftFilters.regionIds.length > 0 ? new Set(draftFilters.regionIds) : null;

    return data.filterOptions.salons.filter((item) => !regionScope || regionScope.has(item.regionId));
  }, [data, draftFilters.regionIds]);

  const sortedRows = useMemo(() => {
    const rows = data?.rows ?? [];

    return [...rows].sort((left, right) => {
      for (const sort of sorts) {
        const result = compareRows(left, right, sort);
        if (result !== 0) {
          return sort.direction === 'asc' ? result : -result;
        }
      }

      return 0;
    });
  }, [data?.rows, sorts]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const paginatedRows = sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const paginationItems = getPaginationItems(currentPage, pageCount);
  const currentRangeStart = sortedRows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const currentRangeEnd = Math.min(currentPage * pageSize, sortedRows.length);

  function toggleDraftSelection(key: 'regionIds' | 'salonIds', value: string) {
    setDraftFilters((current) => {
      const exists = current[key].includes(value);
      const nextValues = exists
        ? current[key].filter((item) => item !== value)
        : [...current[key], value];

      if (key === 'regionIds') {
        const allowedSalonIds = new Set(
          (data?.filterOptions.salons ?? [])
            .filter((item) => nextValues.length === 0 || nextValues.includes(item.regionId))
            .map((item) => item.id),
        );

        return {
          ...current,
          regionIds: nextValues,
          salonIds: current.salonIds.filter((item) => allowedSalonIds.has(item)),
        };
      }

      return {
        ...current,
        [key]: nextValues,
      };
    });
  }

  function applyFilters() {
    setPage(1);
    setFilters(draftFilters);
    setIsFilterSheetOpen(false);
  }

  function resetFilters() {
    setPage(1);
    setDraftFilters(DEFAULT_FILTERS);
  }

  function handleSortClick(key: SortKey, event: MouseEvent<HTMLButtonElement>) {
    setPage(1);
    setSorts((current) => {
      const existing = current.find((item) => item.key === key);
      const nextDirection = existing?.direction === 'asc' ? 'desc' : 'asc';

      if (event.shiftKey) {
        if (existing) {
          return current.map((item) => item.key === key ? { ...item, direction: nextDirection } : item);
        }

        return [...current, { key, direction: key === 'totalParticipants' ? 'desc' : 'asc' }];
      }

      return [{ key, direction: existing ? nextDirection : key === 'totalParticipants' ? 'desc' : 'asc' }];
    });
  }

  function getSortDirection(key: SortKey) {
    return sorts.find((item) => item.key === key)?.direction ?? 'desc';
  }

  function isSortActive(key: SortKey) {
    return sorts.some((item) => item.key === key);
  }

  function applyRegionFilter(regionId: string) {
    const isSameSingleRegion =
      filters.regionIds.length === 1
      && filters.regionIds[0] === regionId
      && filters.salonIds.length === 0;

    const nextFilters = isSameSingleRegion
      ? {
          ...filters,
          regionIds: [],
          salonIds: [],
        }
      : {
          ...filters,
          regionIds: [regionId],
          salonIds: [],
        };

    setPage(1);
    setDraftFilters(nextFilters);
    setFilters(nextFilters);
  }

  const activeFilterChips = [
    filters.startDate || filters.endDate
      ? {
          key: 'period',
          label: `Période : ${filters.startDate || '...'} → ${filters.endDate || '...'}`,
          onClear: () => {
            setPage(1);
            setDraftFilters((current) => ({ ...current, endDate: '', startDate: '' }));
            setFilters((current) => ({ ...current, endDate: '', startDate: '' }));
          },
        }
      : null,
    filters.salonIds.length > 0
      ? {
          key: 'salons',
          label: `${filters.salonIds.length} salon(s)`,
          onClear: () => {
            setPage(1);
            setDraftFilters((current) => ({ ...current, salonIds: [] }));
            setFilters((current) => ({ ...current, salonIds: [] }));
          },
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; onClear: () => void }>;

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2Icon className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <section className="flex min-h-[calc(100svh-10rem)] flex-col gap-6 text-foreground">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Statistiques des participants par région
          </h1>
          <p className="text-sm text-muted-foreground">
            {isAdmin
              ? 'Vision consolidée des participations par région, salon et période pour le pilotage marketing et terrain.'
              : 'Vision consolidée des participations de vos régions, par salon et par période.'}
          </p>
          <p className="text-sm font-medium text-foreground/80">
            {data?.resultsLabel ?? '0 salons • 0 régions • 0 participants'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <ToolbarButton onClick={() => setIsFilterSheetOpen(true)} className="gap-2">
            Filtres
            <FilterIcon className="size-4" />
          </ToolbarButton>
          <Button
            type="button"
            onClick={() => {
              if (!data) {
                return;
              }

              exportDashboardWorkbook(data);
              toast.success('Export XLS lancé.');
            }}
            className="h-12 border-black bg-black px-6 text-[15px] font-medium text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80"
          >
            <DownloadIcon className="mr-2 size-4" />
            Exportez
          </Button>
        </div>
      </div>

      {activeFilterChips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {activeFilterChips.map((filter) => (
            <button
              key={filter.key}
              type="button"
              onClick={filter.onClear}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:border-foreground/20 hover:bg-muted"
            >
              <span>{filter.label}</span>
              <XIcon className="size-3.5 text-muted-foreground" />
            </button>
          ))}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="flex min-h-[12.5rem] flex-col justify-between rounded-[2rem] border border-border bg-card p-6 shadow-sm">
          <div className="space-y-4">
            <div className="flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <UsersIcon className="size-5" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Total participants</p>
              <p className="text-3xl font-semibold text-foreground">{data?.kpis.totalParticipants.toLocaleString('fr-FR') ?? '0'}</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">Sur la période sélectionnée</p>
        </div>

        <div className="flex min-h-[12.5rem] flex-col justify-between rounded-[2rem] border border-border bg-card p-6 shadow-sm">
          <div className="space-y-4">
            <div className="flex size-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
              <TargetIcon className="size-5" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Taux de participation moyen</p>
              <p className="text-3xl font-semibold text-foreground">{formatPercent(data?.kpis.averageParticipationRate ?? 0)}</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">Visiteurs ayant joué au moins une fois</p>
        </div>

        <div className="flex min-h-[12.5rem] flex-col justify-between rounded-[2rem] border border-border bg-card p-6 shadow-sm">
          <div className="space-y-4">
            <div className="flex size-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
              <CalendarRangeIcon className="size-5" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Nombre de salons actifs</p>
              <p className="text-3xl font-semibold text-foreground">{data?.kpis.activeSalons.toLocaleString('fr-FR') ?? '0'}</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Sur la période • {data?.kpis.upcomingSalons ?? 0} à venir
          </p>
        </div>

        <div className="flex min-h-[12.5rem] flex-col justify-between rounded-[2rem] border border-border bg-card p-6 shadow-sm">
          <div className="space-y-4">
            <div className="flex size-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
              <MapPinIcon className="size-5" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Région la plus active</p>
              <p className="text-2xl font-semibold text-foreground">{data?.kpis.topRegion?.regionName ?? '—'}</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {data?.kpis.topRegion
              ? `${data.kpis.topRegion.participants.toLocaleString('fr-FR')} participants • ${data.kpis.topRegion.salonCount} salons`
              : 'Aucune donnée sur la période'}
          </p>
          {data?.kpis.topRegion ? (
            <div className="pt-3">
              <Button
                type="button"
                variant="outline"
                className="h-10 px-4 text-sm"
                onClick={() => applyRegionFilter(data.kpis.topRegion!.regionId)}
              >
                {filters.regionIds.length === 1 && filters.regionIds[0] === data.kpis.topRegion.regionId
                  ? 'Retirer le filtre'
                  : 'Filtrer sur cette région'}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[2rem] border border-border bg-card">
        <div className="border-b border-border/70 bg-[#f4f4f538] p-5 dark:bg-muted/40">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold text-foreground">Participation par région</h2>
              <p className="text-sm text-muted-foreground">
                Compare les performances inter-régions et identifie les zones les plus dynamiques.
              </p>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <table className="min-w-full border-separate border-spacing-0 text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                <th className="px-6 py-4">
                  <SortHeaderButton active={isSortActive('regionName')} direction={getSortDirection('regionName')} onClick={(event) => handleSortClick('regionName', event)}>
                    Région
                  </SortHeaderButton>
                </th>
                <th className="px-6 py-4">
                  <SortHeaderButton active={isSortActive('salonCount')} direction={getSortDirection('salonCount')} onClick={(event) => handleSortClick('salonCount', event)}>
                    Nb salon
                  </SortHeaderButton>
                </th>
                <th className="px-6 py-4">
                  <SortHeaderButton active={isSortActive('totalParticipants')} direction={getSortDirection('totalParticipants')} onClick={(event) => handleSortClick('totalParticipants', event)}>
                    Total participants
                  </SortHeaderButton>
                </th>
                <th className="px-6 py-4">
                  <SortHeaderButton active={isSortActive('participationRate')} direction={getSortDirection('participationRate')} onClick={(event) => handleSortClick('participationRate', event)}>
                    Taux participation
                  </SortHeaderButton>
                </th>
                <th className="px-6 py-4">
                  <SortHeaderButton active={isSortActive('averageParticipantsPerSalon')} direction={getSortDirection('averageParticipantsPerSalon')} onClick={(event) => handleSortClick('averageParticipantsPerSalon', event)}>
                    Moy. participants/salon
                  </SortHeaderButton>
                </th>
                <th className="px-6 py-4">
                  <SortHeaderButton active={isSortActive('evolution')} direction={getSortDirection('evolution')} onClick={(event) => handleSortClick('evolution', event)}>
                    Évolution
                  </SortHeaderButton>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-muted-foreground">
                    Aucune statistique ne correspond aux filtres actifs.
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row) => (
                  <tr key={row.regionId} className="border-t border-border">
                    <td className="border-t border-border px-6 py-5 align-middle">
                      <button
                        type="button"
                        onClick={() => applyRegionFilter(row.regionId)}
                        className="font-semibold text-foreground transition-colors hover:text-primary"
                      >
                        {row.regionName}
                      </button>
                    </td>
                    <td className="border-t border-border px-6 py-5 align-middle text-foreground">{row.salonCount}</td>
                    <td className="border-t border-border px-6 py-5 align-middle text-foreground">{row.totalParticipants.toLocaleString('fr-FR')}</td>
                    <td className="border-t border-border px-6 py-5 align-middle text-foreground">{formatPercent(row.participationRate)}</td>
                    <td className="border-t border-border px-6 py-5 align-middle text-foreground">{row.averageParticipantsPerSalon.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}</td>
                    <td className="border-t border-border px-6 py-5 align-middle">
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2.5 py-1 text-xs font-semibold',
                          row.evolution === null
                            ? 'bg-muted text-muted-foreground'
                            : row.evolution >= 0
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
                              : 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300',
                        )}
                      >
                        {formatEvolution(row.evolution)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div id="dashboard-pagination" className="grid gap-3 border-t border-border px-6 py-4 md:grid-cols-[1fr_auto_1fr] md:items-center">
          <p className="text-sm text-muted-foreground">
            {currentRangeStart}-{currentRangeEnd} sur {sortedRows.length} régions affichées
          </p>

          <Pagination className="justify-center">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#dashboard-pagination"
                  aria-disabled={currentPage <= 1}
                  tabIndex={currentPage <= 1 ? -1 : 0}
                  className={cn(currentPage <= 1 ? 'pointer-events-none opacity-50' : undefined)}
                  onClick={(event) => {
                    event.preventDefault();
                    if (currentPage <= 1) return;
                    setPage((value) => value - 1);
                  }}
                />
              </PaginationItem>

              {paginationItems.map((pageNumber, index) => {
                const previousPage = paginationItems[index - 1];
                const shouldRenderEllipsis = previousPage && pageNumber - previousPage > 1;

                return (
                  <Fragment key={pageNumber}>
                    {shouldRenderEllipsis ? (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : null}
                    <PaginationItem>
                      <PaginationLink
                        href="#dashboard-pagination"
                        isActive={pageNumber === currentPage}
                        onClick={(event) => {
                          event.preventDefault();
                          setPage(pageNumber);
                        }}
                      >
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  </Fragment>
                );
              })}

              <PaginationItem>
                <PaginationNext
                  href="#dashboard-pagination"
                  aria-disabled={currentPage >= pageCount}
                  tabIndex={currentPage >= pageCount ? -1 : 0}
                  className={cn(currentPage >= pageCount ? 'pointer-events-none opacity-50' : undefined)}
                  onClick={(event) => {
                    event.preventDefault();
                    if (currentPage >= pageCount) return;
                    setPage((value) => value + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>

          <div className="flex justify-start md:justify-end">
            <label className="relative">
              <select
                value={pageSize}
                onChange={(event) => {
                  setPage(1);
                  setPageSize(Number(event.target.value) as (typeof pageSizeOptions)[number]);
                }}
                className="h-10 appearance-none rounded-full border border-border bg-background pl-4 pr-10 text-sm font-medium text-foreground shadow-none outline-none transition-colors hover:border-foreground/20 hover:bg-muted"
              >
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option} lignes
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            </label>
          </div>
        </div>
      </div>

      <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
        <SheetContent side="right" className="w-full border-border sm:max-w-md">
          <SheetHeader className="border-b border-border">
            <SheetTitle>Filtres</SheetTitle>
            <SheetDescription>
              Affinez les statistiques par période, région et salon.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-8 overflow-y-auto px-6 py-6">
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Période</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  type="date"
                  value={draftFilters.startDate}
                  onChange={(event) => setDraftFilters((current) => ({ ...current, startDate: event.target.value }))}
                />
                <Input
                  type="date"
                  value={draftFilters.endDate}
                  onChange={(event) => setDraftFilters((current) => ({ ...current, endDate: event.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Région</h3>
              <div className="max-h-56 space-y-2 overflow-y-auto rounded-2xl border border-border p-3">
                {regionOptions.map((option) => (
                  <label key={option.id} className="flex items-center gap-3 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={draftFilters.regionIds.includes(option.id)}
                      onChange={() => toggleDraftSelection('regionIds', option.id)}
                    />
                    <span>{option.name}</span>
                  </label>
                ))}
                {regionOptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucune région disponible.</p>
                ) : null}
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Salon</h3>
              <div className="max-h-64 space-y-2 overflow-y-auto rounded-2xl border border-border p-3">
                {salonOptions.map((option) => (
                  <label key={option.id} className="flex items-center gap-3 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={draftFilters.salonIds.includes(option.id)}
                      onChange={() => toggleDraftSelection('salonIds', option.id)}
                    />
                    <span>{option.name}</span>
                  </label>
                ))}
                {salonOptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucun salon disponible.</p>
                ) : null}
              </div>
            </div>
          </div>

          <SheetFooter className="border-t border-border">
            <Button variant="outline" onClick={resetFilters}>
              Réinitialiser
            </Button>
            <Button onClick={applyFilters}>Appliquer</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  );
}
